import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useSections } from '@/features/sections/sections.queries';
import { useAuth } from '@/features/auth/AuthContext';
import { useUiStore } from '@/stores/ui.store';
import { cn } from '@/lib/utils';

/** A section's colour is stored as a bare HSL triple (`199 89% 42%`). */
const dot = (color) => ({ backgroundColor: `hsl(${color || 'var(--section-hq)'})` });

/**
 * Section switcher — docs/03 §5.
 *
 * Lists only the sections the user holds, plus "All sections" when they have global
 * scope. **A user with exactly one section never sees the switcher**, because a control
 * with a single option is noise.
 *
 * Switching clears the query cache: every list is section-scoped, and showing Agency's
 * invoices under the Real Estate header for even one frame is worse than a brief spinner.
 */
export const SectionSwitcher = ({ collapsed = false }) => {
  const { t } = useTranslation('nav');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: sections = [], isLoading } = useSections();

  const activeSectionId = useUiStore((state) => state.activeSectionId);
  const setActiveSection = useUiStore((state) => state.setActiveSection);

  const hasGlobalScope = !!user?.access?.hasGlobalScope;
  const active = sections.find((section) => section.id === activeSectionId) || null;

  const choose = (sectionId) => {
    if (sectionId === activeSectionId) return;
    setActiveSection(sectionId);
    queryClient.clear();
  };

  if (isLoading) {
    return <div className="h-9 animate-pulse rounded-md bg-muted" aria-hidden="true" />;
  }

  // Nothing to switch between.
  if (!sections.length) {
    return collapsed ? null : (
      <p className="px-2 py-1.5 text-caption text-muted-foreground">{t('sectionSwitcher.empty')}</p>
    );
  }
  if (sections.length === 1 && !hasGlobalScope) {
    const only = sections[0];
    return collapsed ? (
      <div className="flex justify-center py-1">
        <span className="size-2.5 rounded-full" style={dot(only.color)} aria-hidden="true" />
      </div>
    ) : (
      <div className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm">
        <span className="size-2.5 shrink-0 rounded-full" style={dot(only.color)} aria-hidden="true" />
        <span className="truncate font-medium">{only.name}</span>
      </div>
    );
  }

  const label = active?.name ?? t('sectionSwitcher.all');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn('w-full justify-between gap-2', collapsed && 'justify-center px-0')}
          aria-label={t('sectionSwitcher.label')}
        >
          <span className="flex min-w-0 items-center gap-2">
            {active ? (
              <span className="size-2.5 shrink-0 rounded-full" style={dot(active.color)} aria-hidden="true" />
            ) : (
              <Globe className="size-4 shrink-0 text-muted-foreground" />
            )}
            {collapsed ? null : <span className="truncate">{label}</span>}
          </span>
          {collapsed ? null : <ChevronsUpDown className="size-4 shrink-0 opacity-50" />}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{t('sectionSwitcher.label')}</DropdownMenuLabel>

        {hasGlobalScope ? (
          <>
            <DropdownMenuItem onSelect={() => choose(null)}>
              <Globe className="size-4" />
              <span className="flex-1">{t('sectionSwitcher.all')}</span>
              {activeSectionId === null ? <Check className="size-4" /> : null}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}

        {sections.map((section) => (
          <DropdownMenuItem key={section.id} onSelect={() => choose(section.id)}>
            <span className="size-2.5 rounded-full" style={dot(section.color)} aria-hidden="true" />
            <span className="flex-1 truncate">{section.name}</span>
            {activeSectionId === section.id ? <Check className="size-4" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SectionSwitcher;
